import { type Timestamp } from '@principle-theorem/shared';
import {
  type IImportProposal,
  type IImportProposedValue,
  type ImportProposalBooleanField,
  type ImportProposalProseField,
  type ImportProposalWindowHoursField,
  type SectionQaPairLists,
} from './import-proposal';
import {
  type IImportExistingContent,
  type ImportFailureReason,
} from './import-request';
import { type IWebsiteSnapshot } from './website-snapshot';

/** What an import passes to the extractor alongside the snapshot. */
export interface IImportExtractionOptions {
  /**
   * A failed attempt is only retried before this moment, on the same clock
   * as `now`, so the retry still fits within the import callable's time limit.
   */
  retryCutOff: Timestamp;
  now: () => Timestamp;
  /**
   * What the practice's Knowledge Base already covers. The model is shown it
   * so that it offers an update to an existing Q&A pair or custom section,
   * and only when the website says something it does not, instead of
   * proposing it again in other words.
   */
  existingContent: IImportExistingContent;
  /** Ties the extraction log to its practice. It is never sent to OpenAI. */
  practiceId?: string;
}

export interface IImportProposalExtracted {
  ok: true;
  proposal: IImportProposal;
}

export interface IImportProposalNotExtracted {
  ok: false;
  /**
   * A Website Import failure reason, so the callable can return it as is.
   * `fetch-failed` is not among them: reading the website is not the
   * extractor's job.
   */
  reason: Exclude<ImportFailureReason, ImportFailureReason.FetchFailed>;
}

export type ImportProposalExtraction =
  | IImportProposalExtracted
  | IImportProposalNotExtracted;

/**
 * Turns a Website Snapshot into an Import Proposal. The import callable
 * depends on this interface rather than on OpenAI, so its tests use a fake
 * and never pay for a model call.
 */
export interface IImportProposalExtractor {
  extract(
    snapshot: IWebsiteSnapshot,
    options: IImportExtractionOptions
  ): Promise<ImportProposalExtraction>;
}

/**
 * The structured output the extraction model returns. This is OpenAI's wire
 * shape: strict mode makes every property required, so a value the website
 * does not support arrives as JSON null.
 */
export interface IImportExtractionOutput {
  fields: IImportExtractionOutputFields;
  sectionQaPairs: Required<SectionQaPairLists<IImportExtractionSectionQaPair>>;
  customSections: IImportExtractionCustomSection[];
}

export interface IImportExtractionOutputFields
  extends Record<
    ImportProposalProseField,
    IImportProposedValue<string> | null
  > {
  allowsCancellations: IImportProposedValue<boolean> | null;
  allowsRescheduling: IImportProposedValue<boolean> | null;
  cancellationFeeWindowHours: IImportProposedValue<number> | null;
  reschedulingWindowHours: IImportProposedValue<number> | null;
}

export interface IImportExtractionQaPair {
  question: string;
  answer: string;
  provenance: string;
}

/**
 * Only a pair in a sectionQaPairs group can update an existing question: the
 * pairs inside custom sections are never sent as existing content.
 */
export interface IImportExtractionSectionQaPair
  extends IImportExtractionQaPair {
  existingId: string | null;
}

export interface IImportExtractionCustomSection {
  title: string;
  content: string;
  provenance: string;
  qa: IImportExtractionQaPair[];
  existingIndex: number | null;
}

export interface IImportExtractionInput {
  /** The pages as the model reads them, most useful first. */
  content: string;
  /**
   * The pages in `content`. A value can only have been read from a page the
   * model was given, so these are the only Provenance a value may cite.
   */
  pageUrls: string[];
  /** The existing questions and custom sections, as the model reads them. */
  existingContentMessage: string;
}

export interface IImportExtractionParsedAttempt {
  parsed: true;
  output: IImportExtractionOutput;
}

export interface IImportExtractionFailedAttempt {
  parsed: false;
  /** False when sending the same request again would fail the same way. */
  retryable: boolean;
}

export type ImportExtractionAttempt =
  | IImportExtractionParsedAttempt
  | IImportExtractionFailedAttempt;

/**
 * What every extraction attempt logs; the line for a valid answer adds
 * `IImportProposalDropCounts`. Page text, existing content and proposed
 * values are never logged: they are the practice's content, and page text is
 * untrusted.
 */
export interface IImportExtractionLogEntry {
  practiceId?: string;
  /** Characters of page text sent, which `IMPORT_EXTRACTION_MAX_INPUT_CHARS` caps. */
  inputChars: number;
  existingQaPairs: number;
  existingCustomSections: number;
  promptTokens?: number;
  completionTokens?: number;
  attempt: number;
}

/**
 * How many items each check on the model's proposal left out, counted as the
 * review lists them: one per field, Q&A pair and custom section. They go on
 * the log line of the attempt that gave the answer, so a live import shows
 * which check removed a value the model did propose.
 */
export interface IImportProposalDropCounts {
  droppedByProvenance: number;
  droppedByGrounding: number;
  droppedByNames: number;
  droppedNonAnswers: number;
  droppedByCaps: number;
  droppedUnchanged: number;
}

export interface IImportCheckedProposal {
  proposal: IImportProposal;
  /**
   * Whether anything was left before the updates that change nothing were
   * left out. When only those were left, the website was read and understood,
   * so the import is not reported as having no usable content.
   */
  hasUsableContent: boolean;
  droppedCounts: IImportProposalDropCounts;
}

export interface IImportExtractionAnswer {
  output: IImportExtractionOutput;
  logEntry: IImportExtractionLogEntry;
}

/** A change to an appointment a practice can have a policy on. */
export interface IImportPolicyTopic {
  /** What a page must mention for a value about this policy to come from it. */
  mentionPattern: RegExp;
  policyField: ImportProposalProseField;
  allowsField: ImportProposalBooleanField;
  windowField: ImportProposalWindowHoursField;
}
